import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiLock } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import {
  formatCurrency,
  getShippingCost,
  siteConfig,
} from "../config/siteConfig";
import "../css/checkout.css";

const initialFields = {
  email: "",
  country: "Hong Kong",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  address2: "",
  city: "",
  postcode: "",
};

const deliveryCountries = [
  "Hong Kong",
  "Australia",
  "Austria",
  "Belgium",
  "Canada",
  "Denmark",
  "Finland",
  "France",
  "Germany",
  "Ireland",
  "Italy",
  "Japan",
  "Malaysia",
  "Netherlands",
  "New Zealand",
  "Norway",
  "Portugal",
  "Singapore",
  "South Korea",
  "Spain",
  "Sweden",
  "Switzerland",
  "Taiwan",
  "Thailand",
  "United Kingdom",
  "United States",
];

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = lines.reduce(
    (total, line) => total + line.product.price * line.quantity,
    0
  );
  const standardShipping = getShippingCost(subtotal);
  const shipping = standardShipping;
  const total = subtotal + shipping;

  const updateField = (event) => {
    const { name, value } = event.target;
    setFields((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!/^\S+@\S+\.\S+$/.test(fields.email))
      nextErrors.email = "Enter a valid email address.";
    if (!fields.firstName.trim()) nextErrors.firstName = "Enter a first name.";
    if (!fields.lastName.trim()) nextErrors.lastName = "Enter a last name.";
    if (!fields.address.trim())
      nextErrors.address = "Enter a delivery address.";
    if (!fields.city.trim()) nextErrors.city = "Enter a city.";
    if (!fields.postcode.trim()) nextErrors.postcode = "Enter a postcode.";
    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      const firstError = Object.keys(nextErrors)[0];
      document.getElementById(firstError)?.focus();
      return;
    }

    setIsSubmitting(true);
    const orderNumber = `${Date.now()}${Math.floor(
      100000 + Math.random() * 900000
    )}`;
    const successUrl = `${window.location.origin}/payment?state=success`;
    const cancelUrl = `${window.location.origin}/payment?state=cancel`;
    const params = new URLSearchParams({
      orderNo: orderNumber,
      amount: total.toFixed(2),
      currency: siteConfig.currency,
      email: fields.email,
      success_url: successUrl,
      cancel_url: cancelUrl,
      title: lines[0].product.name,
    });
    window.location.assign(
      `${window.location.origin}/create_payment.php?${params.toString()}`
    );
  };

  if (lines.length === 0) {
    return (
      <main id="main-content" className="checkout-empty">
        <p className="eyebrow">Checkout</p>
        <h1>There is nothing to check out yet.</h1>
        <Link className="button-outline" to="/shop">
          Return to shop <FiArrowRight aria-hidden="true" />
        </Link>
      </main>
    );
  }

  const inputProps = (name) => ({
    id: name,
    name,
    value: fields[name],
    onChange: updateField,
    "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? `${name}-error` : undefined,
  });

  return (
    <main id="main-content" className="checkout-page">
      <div className="checkout-page__layout">
        <section
          className="checkout-form-panel"
          aria-labelledby="checkout-heading"
        >
          <Link className="checkout-back text-link" to="/cartpage">
            <FiArrowLeft aria-hidden="true" /> Back to basket
          </Link>
          <header>
            <p className="eyebrow">Secure checkout / 01</p>
            <h1 id="checkout-heading">Delivery & payment</h1>
          </header>

          <form className="checkout-form" onSubmit={handleSubmit} noValidate>
            <fieldset>
              <legend>Contact</legend>
              <div className="checkout-field checkout-field--wide">
                <label htmlFor="email">Email address</label>
                <input
                  {...inputProps("email")}
                  type="email"
                  autoComplete="email"
                />
                {errors.email && <span id="email-error">{errors.email}</span>}
              </div>
            </fieldset>

            <fieldset>
              <legend>Delivery address</legend>
              <div className="checkout-field checkout-field--wide">
                <label htmlFor="country">Country / region</label>
                <select {...inputProps("country")} autoComplete="country-name">
                  {deliveryCountries.map((country) => (
                    <option key={country}>{country}</option>
                  ))}
                </select>
              </div>
              <div className="checkout-field">
                <label htmlFor="firstName">First name</label>
                <input {...inputProps("firstName")} autoComplete="given-name" />
                {errors.firstName && (
                  <span id="firstName-error">{errors.firstName}</span>
                )}
              </div>
              <div className="checkout-field">
                <label htmlFor="lastName">Last name</label>
                <input {...inputProps("lastName")} autoComplete="family-name" />
                {errors.lastName && (
                  <span id="lastName-error">{errors.lastName}</span>
                )}
              </div>
              <div className="checkout-field checkout-field--wide">
                <label htmlFor="company">
                  Company <small>Optional</small>
                </label>
                <input {...inputProps("company")} autoComplete="organization" />
              </div>
              <div className="checkout-field checkout-field--wide">
                <label htmlFor="address">Address</label>
                <input
                  {...inputProps("address")}
                  autoComplete="address-line1"
                />
                {errors.address && (
                  <span id="address-error">{errors.address}</span>
                )}
              </div>
              <div className="checkout-field checkout-field--wide">
                <label htmlFor="address2">
                  Apartment, suite, etc. <small>Optional</small>
                </label>
                <input
                  {...inputProps("address2")}
                  autoComplete="address-line2"
                />
              </div>
              <div className="checkout-field">
                <label htmlFor="city">City</label>
                <input {...inputProps("city")} autoComplete="address-level2" />
                {errors.city && <span id="city-error">{errors.city}</span>}
              </div>
              <div className="checkout-field">
                <label htmlFor="postcode">Postcode</label>
                <input {...inputProps("postcode")} autoComplete="postal-code" />
                {errors.postcode && (
                  <span id="postcode-error">{errors.postcode}</span>
                )}
              </div>
            </fieldset>

            <fieldset>
              <legend>Shipping method</legend>
              <label className="shipping-choice">
                <input
                  type="radio"
                  name="shippingMethod"
                  value="standard"
                  checked
                  readOnly
                />
                <span>
                  <strong>Tracked delivery</strong>
                  <small>{siteConfig.shipping.dispatch}</small>
                </span>
                <b>{formatCurrency(standardShipping)}</b>
              </label>
            </fieldset>

            <fieldset>
              <legend>Payment</legend>
              <p className="checkout-form__notice">
                <FiLock aria-hidden="true" /> You will enter payment details
                securely on our payment provider's page.
              </p>
            </fieldset>

            <button
              className="checkout-submit"
              type="submit"
              disabled={isSubmitting}
            >
              <span>{isSubmitting ? "Redirecting…" : "Pay now"}</span>
              <span>
                {formatCurrency(total)} <FiArrowRight aria-hidden="true" />
              </span>
            </button>
          </form>
        </section>

        <aside
          className="checkout-order"
          aria-labelledby="order-summary-heading"
        >
          <h2 id="order-summary-heading">Order summary</h2>
          <div className="checkout-order__lines">
            {lines.map((line) => (
              <article key={`${line.id}-${line.size}-${line.color}`}>
                <div className="checkout-order__image">
                  <img
                    src={line.product.image}
                    alt=""
                    width="1120"
                    height="1400"
                    loading="lazy"
                    decoding="async"
                  />
                  <span>{line.quantity}</span>
                </div>
                <div>
                  <h3>{line.product.name}</h3>
                  <em>{line.product.maker}</em>
                  <p>{[line.color, line.size].filter(Boolean).join(" / ")}</p>
                </div>
                <strong>
                  {formatCurrency(line.product.price * line.quantity)}
                </strong>
              </article>
            ))}
          </div>
          <dl>
            <div>
              <dt>Subtotal</dt>
              <dd>{formatCurrency(subtotal)}</dd>
            </div>
            <div>
              <dt>Shipping</dt>
              <dd>
                {shipping === 0 ? "Complimentary" : formatCurrency(shipping)}
              </dd>
            </div>
            <div className="checkout-order__total">
              <dt>
                Total <small>{siteConfig.currency}</small>
              </dt>
              <dd>{formatCurrency(total)}</dd>
            </div>
          </dl>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
